/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDispatch } from 'react-redux';

import Heading from '../../components/heading/Heading';
import Item from '../../components/custom/Item';
import CustomNoRowsOlverlay from '../../components/custom/CustomNoRowsOlverlay';
import msgNotificacion from '../../scripts/notification';
import msgAlerta from '../../scripts/alert';
import Formatter from '../../scripts/formatters';
import ClientesService from '../../services/clientesService';
import SeguridadService from '../../services/seguridadService';
import { showProgress } from '../../redux/reducers/progress.Slice';
import { PAGE_SIZE, NAME_PROJECT } from '../../redux/constants/index';
import '../../styles/clientes.css';

export default function AsociarVendedor() {
  const dispatch = useDispatch();
  const userLogin = JSON.parse(localStorage.getItem('user'));
  const esAdminRrhh = [1, 2].includes(Number(userLogin?.CodigoRol));

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({
    idUsuarioVendedor: null,
    usuario: null,
    vendedor: null,
  });

  const opcionesVendedor = useMemo(
    () =>
      (vendedores || []).map((v) => ({
        id: v.CodVendedor,
        label: `${v.CodVendedor} — ${v.NombreVendedor}`,
      })),
    [vendedores]
  );

  const opcionesVendedorModal = useMemo(() => {
    if (!form.usuario?.id || form.idUsuarioVendedor) return opcionesVendedor;
    const asignados = rows
      .filter((r) => r.IdUsuario === form.usuario.id)
      .map((r) => String(r.CodVendedor || '').trim().toUpperCase());
    return opcionesVendedor.filter(
      (v) => !asignados.includes(String(v.id || '').trim().toUpperCase())
    );
  }, [form.usuario, form.idUsuarioVendedor, opcionesVendedor, rows]);

  const opcionesUsuario = useMemo(
    () =>
      (usuarios || []).map((u) => ({
        id: u.IdUsuario,
        label: `${u.Nombre || u.UserLogin} — ${u.NombreCompleto || u.Nombre}`,
      })),
    [usuarios]
  );

  const cargarDatos = async () => {
    if (!esAdminRrhh) return;
    try {
      setLoading(true);
      dispatch(showProgress(true));

      const [resAsoc, resUsers, resVend] = await Promise.all([
        ClientesService.getAsociacionesUsuarioVendedor({ codigoRol: userLogin.CodigoRol }),
        SeguridadService.getUsuarios({ codigoRol: 0 }),
        ClientesService.getVendedores(),
      ]);

      if (resAsoc?.Codigo === 1) setRows(resAsoc.Data || []);
      else setRows([]);

      if (resUsers?.Codigo === 1) setUsuarios(resUsers.Data || []);
      if (resVend?.Codigo === 1) setVendedores(resVend.Data || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
      dispatch(showProgress(false));
    }
  };

  useEffect(() => {
    document.title = `${NAME_PROJECT} | Asociar vendedor`;
    if (!esAdminRrhh) {
      msgNotificacion('Solo Admin o RRHH pueden gestionar asociaciones.', 'warning');
      return;
    }
    cargarDatos();
  }, []);

  const abrirModal = (row = null) => {
    if (row) {
      setForm({
        idUsuarioVendedor: row.IdUsuarioVendedor,
        usuario: { id: row.IdUsuario, label: `${row.Usuario} — ${row.NombreCompleto}` },
        vendedor: { id: row.CodVendedor, label: `${row.CodVendedor} — ${row.NombreVendedor}` },
      });
    } else {
      setForm({ idUsuarioVendedor: null, usuario: null, vendedor: null });
    }
    setModalOpen(true);
  };

  const guardar = async () => {
    if (!form.usuario?.id || !form.vendedor?.id) {
      msgNotificacion('Seleccione usuario y vendedor.', 'warning');
      return;
    }

    try {
      dispatch(showProgress(true));
      const response = await ClientesService.guardarUsuarioVendedor({
        idUsuarioVendedor: form.idUsuarioVendedor,
        idUsuario: form.usuario.id,
        codVendedor: form.vendedor.id,
        idUsuarioCreacion: userLogin.CodigoUsuario,
        codigoRol: userLogin.CodigoRol,
      });

      if (response?.Codigo === 1) {
        msgNotificacion(response.Mensaje, 'success');
        setModalOpen(false);
        cargarDatos();
      } else {
        msgNotificacion(response?.Mensaje || 'No se pudo guardar.', 'warning');
      }
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(showProgress(false));
    }
  };

  const eliminar = (row) => {
    msgAlerta(
      'Confirmar',
      `¿Eliminar asociación de <strong>${row.NombreCompleto}</strong> con vendedor <strong>${row.CodVendedor}</strong>?`,
      'question',
      async () => {
        try {
          dispatch(showProgress(true));
          const response = await ClientesService.eliminarUsuarioVendedor({
            idUsuarioVendedor: row.IdUsuarioVendedor,
            codigoRol: userLogin.CodigoRol,
          });

          if (response?.Codigo === 1) {
            msgNotificacion(response.Mensaje, 'success');
            cargarDatos();
          } else {
            msgNotificacion(response?.Mensaje || 'No se pudo eliminar.', 'warning');
          }
        } catch (error) {
          console.error(error);
        } finally {
          dispatch(showProgress(false));
        }
      }
    );
  };

  const columns = [
    { field: 'IdUsuarioVendedor', headerName: 'ID', width: 70 },
    { field: 'Usuario', headerName: 'Usuario', width: 130 },
    { field: 'NombreCompleto', headerName: 'Nombre', minWidth: 200, flex: 1 },
    { field: 'CodVendedor', headerName: 'Cód. vendedor', width: 120 },
    { field: 'NombreVendedor', headerName: 'Vendedor', minWidth: 200, flex: 1 },
    {
      field: 'FechaCreacion',
      headerName: 'Fecha',
      width: 160,
      valueFormatter: (value) => Formatter.dateTimeFormatter(value),
    },
    {
      field: 'acciones',
      headerName: 'Acciones',
      width: 120,
      sortable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Editar">
            <IconButton size="small" color="primary" onClick={() => abrirModal(row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton size="small" color="error" onClick={() => eliminar(row)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  if (!esAdminRrhh) {
    return (
      <Grid container spacing={1} paddingX={1} paddingTop={1}>
        <Grid item xs={12}>
          <Heading title="Asociar usuario con vendedor" />
        </Grid>
        <Grid item xs={12}>
          <Item>No tiene permisos para esta pantalla.</Item>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={1} paddingX={1} paddingTop={1}>
      <Grid item xs={12}>
        <Heading title="Asociar usuario con vendedor">
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => abrirModal()}>
            Nueva asociación
          </Button>
        </Heading>
      </Grid>

      <Grid item xs={12}>
        <Item>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.IdUsuarioVendedor}
            loading={loading}
            pageSizeOptions={[PAGE_SIZE, 25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: PAGE_SIZE } } }}
            slots={{ noRowsOverlay: CustomNoRowsOlverlay }}
            disableRowSelectionOnClick
            className="MuiPaper-root-dataGrid-clientes"
          />
        </Item>
      </Grid>

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {form.idUsuarioVendedor ? 'Editar asociación' : 'Nueva asociación'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              options={opcionesUsuario}
              value={form.usuario}
              onChange={(_, value) => setForm((prev) => ({ ...prev, usuario: value }))}
              renderInput={(params) => <TextField {...params} label="Usuario" />}
            />
            <Autocomplete
              options={opcionesVendedorModal}
              value={form.vendedor}
              onChange={(_, value) => setForm((prev) => ({ ...prev, vendedor: value }))}
              renderInput={(params) => <TextField {...params} label="Vendedor" />}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={guardar}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
